/**
 * useSeo — lightweight per-route SEO manager (no react-helmet dependency).
 *
 * Injects <title>, description, canonical, Open Graph, Twitter and optional
 * JSON-LD structured data into <head> for the current route, and reverts them
 * on unmount so we never leak stale meta across routes.
 *
 * Usage:
 *   useSeo({
 *     title: "Sarees — Royal Lady",
 *     description: "Handpicked luxury sarees from Kanchipuram, Banarasi and Uppada.",
 *     canonical: "https://royalladyofficial.com/shop?category=sarees",
 *     jsonLd: { "@context": "https://schema.org", "@type": "ItemList", ... },
 *   });
 */
import { useEffect } from "react";

const CANONICAL_HOST = "https://royalladyofficial.com";
const DEFAULT_OG_IMAGE = `${CANONICAL_HOST}/icon-512.png`;

function ensureTag(selector, attrs) {
  let el = document.head.querySelector(selector);
  const created = !el;
  if (created) {
    el = document.createElement(attrs.tag || "meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => {
    if (k === "tag") return;
    if (v == null) el.removeAttribute(k);
    else el.setAttribute(k, v);
  });
  return { el, created };
}

export function useSeo({
  title,
  description,
  canonical,
  ogImage,
  ogType = "website",
  jsonLd,
  robots,
} = {}) {
  useEffect(() => {
    const prev = { title: document.title };
    const created = [];

    if (title) document.title = title;

    const set = (selector, attrs) => {
      const { el, created: madeNew } = ensureTag(selector, attrs);
      if (madeNew) created.push(el);
      return el;
    };

    if (description) {
      set('meta[name="description"]', { tag: "meta", name: "description", content: description });
      set('meta[property="og:description"]', { tag: "meta", property: "og:description", content: description });
      set('meta[name="twitter:description"]', { tag: "meta", name: "twitter:description", content: description });
    }
    if (title) {
      set('meta[property="og:title"]', { tag: "meta", property: "og:title", content: title });
      set('meta[name="twitter:title"]', { tag: "meta", name: "twitter:title", content: title });
    }
    if (canonical) {
      set('link[rel="canonical"]', { tag: "link", rel: "canonical", href: canonical });
      set('meta[property="og:url"]', { tag: "meta", property: "og:url", content: canonical });
    }
    const image = ogImage || DEFAULT_OG_IMAGE;
    set('meta[property="og:image"]', { tag: "meta", property: "og:image", content: image });
    set('meta[name="twitter:image"]', { tag: "meta", name: "twitter:image", content: image });
    set('meta[property="og:type"]', { tag: "meta", property: "og:type", content: ogType });
    if (robots) {
      set('meta[name="robots"]', { tag: "meta", name: "robots", content: robots });
    }

    let jsonLdNode = null;
    if (jsonLd) {
      jsonLdNode = document.createElement("script");
      jsonLdNode.type = "application/ld+json";
      jsonLdNode.setAttribute("data-seo-jsonld", "true");
      jsonLdNode.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(jsonLdNode);
    }

    return () => {
      document.title = prev.title;
      if (jsonLdNode && jsonLdNode.parentNode) jsonLdNode.parentNode.removeChild(jsonLdNode);
      // We intentionally don't remove standard meta tags — the next route's
      // useSeo() will overwrite them; keeping them prevents an empty flash.
    };
    // Recompute on any prop change.
  }, [title, description, canonical, ogImage, ogType, robots, JSON.stringify(jsonLd || null)]);
}

export const SITE_URL = CANONICAL_HOST;

/** Convenience builder for a Product JSON-LD payload. */
export function productJsonLd({
  id,
  name,
  description,
  image,
  images,
  price,
  currency = "INR",
  brand = "Royal Lady",
  sku,
  rating,
  reviewCount,
  inStock = true,
}) {
  const url = `${SITE_URL}/product/${id}`;
  const gallery = (images && images.length ? images : image ? [image] : []).filter(Boolean);
  const payload = {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    description: description || undefined,
    image: gallery.length ? gallery : undefined,
    sku: sku || undefined,
    brand: { "@type": "Brand", name: brand },
    offers: {
      "@type": "Offer",
      url,
      priceCurrency: currency,
      price: typeof price === "number" ? price.toFixed(2) : String(price ?? ""),
      availability: inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
    },
  };
  if (rating && reviewCount) {
    payload.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: Number(rating).toFixed(1),
      reviewCount: Number(reviewCount),
    };
  }
  return payload;
}

/** Organization JSON-LD used on Home / About. */
export const organizationJsonLd = {
  "@context": "https://schema.org",
  "@type": "OnlineStore",
  name: "Royal Lady",
  url: SITE_URL,
  logo: `${SITE_URL}/icon-512.png`,
  slogan: "Timeless luxury for the modern woman.",
  sameAs: [],
  areaServed: { "@type": "Country", name: "India" },
  paymentAccepted: ["UPI", "Credit Card", "Debit Card", "Net Banking", "Wallet", "Cash on Delivery"],
  currenciesAccepted: "INR",
};
